import React from "react";

export interface FallbackProps {
  error: Error;
  resetError?: (...args: Array<unknown>) => void;
}

interface ErrorBoundaryProps {
  FallBackComponent: React.ComponentType<FallbackProps>;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  onReset?: (...args: Array<unknown>) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const initialState: ErrorBoundaryState = { error: null };

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = initialState;
  }

  static getDerivedStateFromError<T extends Error>(error: T) {
    // returns object that's passed to the updated state.
    return { error };
  }

  resetError(...args: Array<unknown>) {
    this.props.onReset?.(...args);
    this.reset();
  }

  reset() {
    this.setState(initialState);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    const { error } = this.state;
    const { FallBackComponent } = this.props;

    if (error !== null) {
      const props = {
        error,
        resetError: () => this.resetError(),
      };

      return <FallBackComponent {...props} />;
    }

    return this.props.children;
  }
}
